import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { formatCurrency } from '../../lib/formatters';

const formatPercent = (value) => {
  return `${(value || 0).toFixed(2)}%`;
};

const PerformanceReport = ({ performanceData }) => {
  if (!performanceData) return null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Avg Deal Size</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {formatCurrency(performanceData.summary?.avg_deal_size)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Collection Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatPercent(performanceData.summary?.collection_rate)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Default Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {formatPercent(performanceData.summary?.default_rate)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Avg Term (Days)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">
              {Math.round(performanceData.summary?.avg_term_days || 0)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Monthly Deal Volume</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={performanceData.monthly_volume || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="#3b82f6" name="Deal Count" />
                <Bar dataKey="amount" fill="#10b981" name="Total Amount" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Broker Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={performanceData.broker_performance || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="broker_name" />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Bar dataKey="total_funded" fill="#8b5cf6" name="Total Funded" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Performance Metrics Table */}
      <Card>
        <CardHeader>
          <CardTitle>Industry Performance</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Industry</TableHead>
                <TableHead>Deal Count</TableHead>
                <TableHead>Total Funded</TableHead>
                <TableHead>Avg Deal Size</TableHead>
                <TableHead>Collection Rate</TableHead>
                <TableHead>Default Rate</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(performanceData.by_industry || []).map((item, index) => (
                <TableRow key={index}>
                  <TableCell className="font-medium">{item.industry || 'Unknown'}</TableCell>
                  <TableCell>{item.deal_count}</TableCell>
                  <TableCell>{formatCurrency(item.total_funded)}</TableCell>
                  <TableCell>{formatCurrency(item.avg_deal_size)}</TableCell>
                  <TableCell className="text-green-600">{formatPercent(item.collection_rate)}</TableCell>
                  <TableCell className="text-red-600">{formatPercent(item.default_rate)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default PerformanceReport;
